import _styled, { css as _css, default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6 } from "styled-components";
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React from "react";
import NextHead from "next/head";
import { __IS_DEV__ } from "~/features/app/utils";
import { screens, type Screen, type ScreenProp, screenProps } from "~/features/app/screens";
import { parseRatio, resizeByRatio, type Ratio } from "~/features/ui/aspect-ratio";
import { type ImageColor, type SanityImageEdit, type SanityImageParams, buildSanityImageSrc, buildSanityImageSrcSet } from "~/features/sanity/media/image-url";
import { optimizeMuxImageUrl } from "~/features/ui/video/utils";
import { ParallaxImg, useImageParallax } from "~/features/ui/image-parallax";
type Sizes = ScreenProp<string>;
export type Props = Omit<React.ComponentPropsWithoutRef<"img">, "sizes"> & {
  src?: string;
  alt?: string;
  ratio?: Ratio;
  sizes?: Sizes;
  width?: number;
  height?: number;
  oriWidth?: number;
  oriHeight?: number;
  focalPoint?: number[];
  imageEdit?: SanityImageEdit;
  palette?: ImageColor[];
  rounded?: boolean;
  imgixProps?: SanityImageParams;
  fit?: keyof typeof imgFit;
  priority?: boolean;
  mimeType?: string;
  onLoad?: (img: HTMLImageElement) => void;
  fetchPriority?: "high" | "low" | "auto";
};
function normalizeImageSizes(sizes?: Sizes) {
  if (!sizes) {
    return "100vw";
  }
  const {
    DEFAULT = "100vw",
    ...rSizes
  } = screenProps(sizes);
  if (Object.keys(rSizes).length === 0) {
    return DEFAULT;
  }
  const queries = Object.entries(rSizes).map(([bp, size]) => {
    return `(min-width: ${screens[bp as Screen]}) ${size}`;
  });
  return `${queries.join(", ")}, ${DEFAULT}`;
}
function useImageLoaded() {
  const ref = React.useRef<HTMLImageElement>(null);
  const [loaded, setLoaded] = React.useState(false);
  const onLoad = () => {
    setLoaded(true);
  };
  React.useEffect(() => {
    if (!ref.current) {
      return;
    }
    if (ref.current.complete) {
      onLoad();
    } else {
      ref.current.onload = () => {
        onLoad();
      };
    }
  }, []);
  return [ref, loaded, onLoad] as const;
}
const imgFit = {
  contain: {
    "height": "auto",
    "maxWidth": "100%"
  },
  fill: {
    "height": "auto",
    "width": "100%"
  },
  cover: {
    "position": "absolute",
    "inset": "0",
    "height": "100%",
    "width": "100%",
    "objectFit": "cover"
  }
} as const;
export function Image(props: Props) {
  const {
    sizes,
    src,
    alt,
    width,
    height,
    focalPoint,
    imageEdit,
    ratio,
    onLoad,
    oriWidth,
    oriHeight,
    fit = "fill",
    priority = false,
    rounded = true,
    loading = "lazy",
    palette = [{
      hex: "#6C7061"
    }],
    imgixProps: userImgixProps,
    mimeType,
    fetchPriority,
    ...rest
  } = props;
  const [imageRef, imageLoaded, onImageLoad] = useImageLoaded();
  const figureRef = React.useRef<HTMLElement>(null);
  const {
    enabled: parallaxEnabled
  } = useImageParallax();
  React.useEffect(() => {
    if (imageLoaded && onLoad && imageRef.current) {
      onLoad(imageRef.current);
    }
  }, [imageLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // styled-components 5 (via @emotion/is-prop-valid) strips fetchpriority
  // from rendered <img>, so set it imperatively after mount to keep LCP
  // images flagged as high priority.
  React.useLayoutEffect(() => {
    if (!imageRef.current) return;
    const value = priority ? "high" : fetchPriority;
    if (value && value !== "auto") {
      imageRef.current.setAttribute("fetchpriority", value);
    }
  }, [priority, fetchPriority]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!src) {
    return null;
  }
  const aspectRatio = parseRatio(ratio);

  // By default we use the original image sizes.
  let size = [oriWidth, oriHeight];

  // Resize all with custom or new aspect ratio.
  if (width && height) {
    size = resizeByRatio(width, height, aspectRatio ?? [width, height]);
  } else if (oriWidth && oriHeight) {
    // 1. Resize height with custom or original aspect ratio.
    // 2. Resize width with custom or original aspect ratio.
    // 3. Resize with custom aspect ratio.
    if (height) {
      size = resizeByRatio(oriWidth, height, aspectRatio ?? [oriWidth, oriHeight]);
    } else if (width) {
      size = resizeByRatio(width, oriHeight, aspectRatio ?? [oriWidth, oriHeight]);
    } else if (aspectRatio) {
      size = resizeByRatio(oriWidth, oriHeight, aspectRatio);
    }
  } else {
    if (__IS_DEV__) {
      console.warn(`Image: Missing image dimensions for ${src}. Make sure to provide either the original or the custom dimensions.`);
    }

    // This should not happen, because we expect that
    // either the original or the custom dimensions are set.
    // return null;
  }

  // If both points use the default value (0.5), we can ignore them.
  // Also, a focal point is only valid if both points are set.
  const [fpX, fpY] = focalPoint && !focalPoint.every(fp => fp === 0.5) ? focalPoint : [];
  // `0` is a valid coordinate (a hotspot pinned to the left/top edge), so check for
  // presence rather than truthiness.
  const hasFocalPoint = typeof fpX === "number" && typeof fpY === "number";

  // SVGs are vectors — routing them through the image CDN transform would rasterize
  // them into bitmaps, losing scalability. They therefore cannot honour an editorial
  // crop either. Serve them directly so the browser renders
  // the native vector. SVGs are tiny files so they are always loaded eagerly.
  if (mimeType === "image/svg+xml") {
    return <>
        {priority && <NextHead>
            <link key={`preload-image-${src}`} rel="preload" as="image" href={src} fetchPriority="high" />
          </NextHead>}
        <_StyledFigure {...rest} $_css2={[{
        "position": "relative"
      }, imgFit[fit]]}>
          <_StyledImg ref={imageRef} onLoad={onImageLoad} src={src} alt={alt ?? ""} loading="eager" draggable={false} $_css3={[imgFit[fit], rounded && {
          "borderRadius": "4px"
        }]} />
        </_StyledFigure>
      </>;
  }

  // GIFs bypass the image CDN transform pipeline, which would flatten the animation.
  // An editorial crop cannot be applied to them for the same reason.
  if (mimeType?.startsWith("image/gif")) {
    return <_StyledImg2 ref={imageRef} onLoad={onImageLoad} src={src} alt={alt ?? ""} width={size[0]} height={size[1]} loading={loading} decoding={loading === "lazy" ? "async" : undefined} draggable={false} $_css4={[imgFit[fit], rounded && {
      "borderRadius": "4px"
    }, aspectRatio && _css(["aspect-ratio:", " / ", ";object-fit:cover;"], aspectRatio[0], aspectRatio[1])]} />;
  }
  const imgixProps: SanityImageParams = {
    // Only resize the image if a custom width or height has been set.
    w: width ? size[0] : undefined,
    h: height ? size[1] : undefined,
    "max-w": 2048,
    "fp-x": fpX,
    "fp-y": fpY,
    ar: aspectRatio ? aspectRatio.join(":") : undefined,
    // Aspect ratio and focal points require the crop mode.
    fit: width && height || aspectRatio || hasFocalPoint ? "crop" : undefined,
    crop: hasFocalPoint ? "focalpoint" : undefined,
    // The editorial crop is resolved into `rect` inside the url builder, i.e. after
    // this merge, so a caller-supplied `fit`/`crop` can no longer defeat it.
    edit: imageEdit,
    ...userImgixProps
  } as const;
  let displaySrc = src;
  if (src.includes("image.mux.com")) {
    const layoutWidth = width ?? size[0] ?? oriWidth ?? 640;
    const muxCap = Math.min(Math.max(Math.ceil(layoutWidth * 2), 1), 1920);
    displaySrc = optimizeMuxImageUrl(src, muxCap);
  }

  // Parallax is opt-in per page (Sanity route toggle → ImageParallaxProvider).
  // SVGs and GIFs already returned above, so this branch only sees
  // CDN-transformable raster images. `priority` (LCP / hero) images stay
  // untransformed. `ParallaxImg` itself skips images that are too small
  // (logos/icons) or carry `data-noparallax`.
  const parallaxActive = parallaxEnabled && !priority;
  const imgAttrs = {
    width: size[0],
    height: size[1],
    loading: (priority ? "eager" : loading) as "eager" | "lazy",
    decoding: (priority ? undefined : loading === "lazy" ? "async" : undefined) as "async" | undefined,
    fetchPriority: (priority ? "high" : fetchPriority) as "high" | "low" | "auto" | undefined,
    sizes: normalizeImageSizes(sizes),
    src: buildSanityImageSrc(displaySrc, imgixProps),
    srcSet: buildSanityImageSrcSet(displaySrc, imgixProps),
    draggable: false
  } as const;
  const imgStyle: React.CSSProperties = {
    objectPosition: fit === "cover" && hasFocalPoint ? `${fpX * 100}% ${fpY * 100}%` : undefined
  };
  return <>
      {priority && <NextHead>
          <link key={`preload-image-${displaySrc}`} rel="preload" as="image" href={displaySrc} imageSrcSet={buildSanityImageSrcSet(displaySrc, imgixProps)} imageSizes={normalizeImageSizes(sizes)} fetchPriority="high" />
        </NextHead>}
      <_StyledFigure2 ref={figureRef} {...rest} $_css5={[{
      "position": "relative"
    }, imgFit[fit], parallaxActive && {
      "overflow": "hidden"
    }, parallaxActive && rounded && {
      "borderRadius": "4px"
    }]}>
        {parallaxActive ? <ParallaxImg containerRef={figureRef} imgRef={imageRef} onLoad={onImageLoad} alt={alt ?? ""} imgCss={[imgFit[fit], rounded && {
        "borderRadius": "4px"
      }]} cover={fit === "cover"} style={imgStyle} {...imgAttrs} /> : <_StyledImg3 ref={imageRef} onLoad={onImageLoad} alt={alt ?? ""} style={imgStyle} {...imgAttrs} $_css6={[imgFit[fit], rounded && {
        "borderRadius": "4px"
      }]} />}

        {!imageLoaded && <_StyledDiv $_css7={[{
        "pointerEvents": "none",
        "position": "absolute",
        "inset": "0",
        "userSelect": "none"
      }, {
        "backgroundSize": "cover",
        "backgroundRepeat": "no-repeat"
      }, _css(["background-image:linear-gradient(45deg,", ");"], palette.map(c => c.hex).join(","))]} />}
      </_StyledFigure2>
    </>;
}
var _StyledFigure = /*#__PURE__*/_styled("figure").withConfig({
  componentId: "sc-sf-sdq2rp-0"
})`${p => p.$_css2}`;
var _StyledImg = _styled2("img").withConfig({
  componentId: "sc-sf-sdq2rp-1"
})`${p => p.$_css3}`;
var _StyledImg2 = _styled3("img").withConfig({
  componentId: "sc-sf-sdq2rp-2"
})`${p => p.$_css4}`;
var _StyledFigure2 = _styled4("figure").withConfig({
  componentId: "sc-sf-sdq2rp-3"
})`${p => p.$_css5}`;
var _StyledImg3 = _styled5("img").withConfig({
  componentId: "sc-sf-sdq2rp-4"
})`${p => p.$_css6}`;
var _StyledDiv = /*#__PURE__*/_styled6("div").withConfig({
  displayName: "image___StyledDiv",
  componentId: "sc-1y8nyym-0"
})(["", ""], p => p.$_css7);